/* eslint-disable */
import React from 'react';
import cls from 'classnames';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { gsap, ScrollToPlugin, TweenMax, Expo } from 'gsap/all';
import GlobalDispatchContext from '~/context/GlobalDispatchContext';
import GlobalStateContext from '~/context/GlobalStateContext';
import { DataLayer } from '~/components/DataLayer';
// import { FiatBrand } from '~/components/FiatBrand';
import { CarBrand } from '~/components/CarBrand';
import { ToggleButton } from '~/components/ToggleButton';
import { PinIcon } from '~/components/PinButton';
import { getScrollTop } from '~/utils/dom/getScrollTop';
import configData from '~/config/data';
// import MenuAnchor from './MenuAnchor';
// import MenuButton from './MenuButton';
// import MenuLink from './MenuLink';
import { getAnchorIndex } from './MenuUtils';
import css from './Menu.scss';

gsap.registerPlugin(ScrollToPlugin);

const classes = {
	globalOpen: 'is-menu-open',
	globalShrink: 'is-menu-shrink',
	root: css.menu,
	foreground: css.menuForeground,
	navBar: css.menuNavBar,
	navBarLogo: css.menuNavBarLogo,
	navBarLogoIcon: css.menuNavBarLogoIcon,
	navBarLabel: css.menuNavBarLabel,
	navBarPin: css.menuNavBarPin,
	navBarPinIcon: css.menuNavBarPinIcon,
	aside: css.menuAside,
	asideList: css.menuAsideList,
	asideListAnchors: css.menuAsideListAnchors,
	asideListLinks: css.menuAsideListLinks,
	asideClose: css.menuAsideClose,
	asideAnchor: css.menuAsideAnchor,
	asideAnchorMask: css.menuAsideAnchorMask,
	asideAnchorText: css.menuAsideAnchorText,
	asideAnchorLink: css.menuAsideAnchorLink,
	asideAnchorLinkText: css.menuAsideAnchorLinkText,
	brand: css.menuBrand,
	brandCar: css.menuBrandCar,
	isActive: css.isActive,
	isOnTop: css.isOnTop,
	isOpen: css.isOpen,
	isShrink: css.isShrink,
	isInvisible: css.isInvisible,
	subitem: css.subitem
};

const propTypes = {
	className: PropTypes.string,
	classes: PropTypes.objectOf(PropTypes.string),
	mobile: PropTypes.bool,
};

const defaultProps = {
	className: '',
	classes,
	data: configData.menu.data,
};

const MENU_LAST_INDEX = 3;

export const Menu = React.forwardRef(
	({ className, classes, mobile, data, ...props }, ref) => {
		const rootRef = React.useRef();
		const navBarRef = React.useRef();
		const globalDispatchContext = React.useContext(GlobalDispatchContext);
		const globalStateContext = React.useContext(GlobalStateContext);
		const prevScrollTop = React.useRef(getScrollTop());
		const [isOnTop, setIsOnTop] = React.useState(prevScrollTop.current === 0);
		const [isShrink, setIsShrink] = React.useState(false);
		const [isOpen, setIsOpen] = React.useState(!mobile);
		const [isInvisible, setIsInvisible] = React.useState(false);
		const [currentTabIndex, setCurrentTabIndex] = React.useState(-1);
		const [currentIndex, setCurrentIndex] = React.useState(0);
		const isClicked = React.useRef(false);
		const [isCloseToTop] = React.useState(
			prevScrollTop.current < window.innerHeight
		);

		const open = (triggerDL = true) => {
			if (triggerDL) DataLayer.push('Menu_ClicouAbrir');
			globalDispatchContext.openAccordionItem();
			setIsOpen(true);
		};

		const close = (triggerDL = true) => {
			if (triggerDL) DataLayer.push('Menu_ClicouFechar');
			setIsOpen(false);
		};

		const toggle = (triggerDL = true) => {
			isOpen ? close(triggerDL) : open(triggerDL);
		};

		const scrollTo = (y = 0, time = 0.5, properties = {}) => {
			TweenMax.to(window, 0.5, {
				scrollTo: { y, autoKill: false },
				...properties,
			});
		};

		const checkAnchors = () => {
			if (isClicked.current) return false;
			const anchors = Array.prototype.slice.call(document.querySelectorAll('[data-ui-anchor]'));
			const index = getAnchorIndex(anchors, 100, mobile);
			if (!mobile && index > MENU_LAST_INDEX) return false;
			if (!mobile) setCurrentIndex(index);
			setCurrentIndex((lastIndex) => {
				if (index !== lastIndex) {
					const anchor = anchors[index];
					const anchorId = anchor && anchor.getAttribute('id');
					if (anchor && anchorId) {
						DataLayer.push(
							'App_Pageview',
							anchor.getAttribute('data-ui-anchor'),
							currentIndex !== index,
							!currentIndex && !index
						);
					}
				}
				return index;
			});
		};

		const onScroll = () => {
			const currentScrollTop = getScrollTop();
			//console.log('- scrollTop:', currentScrollTop);
			// setIsOnTop(currentScrollTop === 0);
			// setIsInvisible(!(prevScrollTop.current > currentScrollTop));
			checkAnchors();
			prevScrollTop.current = currentScrollTop;
		};

		const onClickItem = (index) => {
			isClicked.current = true;
			const item = data.list[index];
			const isSubmenuClicked = !!(item.openTab && item.submenu);
			const scrollPositionY = getScrollTop();
			const $target = document.querySelector(`[data-ui-anchor="${item.anchor}"]`);
			if ($target) {
				const targetBounds = $target.getBoundingClientRect();
				const navBarHeight = navBarRef.current.clientHeight;
				const navBarDiff = mobile ? navBarHeight : 0;
				const scrollPosition = (targetBounds.top + scrollPositionY);
				const itsGoingDown = !(prevScrollTop.current > scrollPosition);
				scrollTo(scrollPosition - navBarDiff, 0.5, {
					onComplete: () => {
						setCurrentIndex(index);
						isClicked.current = false;
						const uiAnchor = $target.getAttribute('data-ui-anchor');
						const vpv = uiAnchor;
						DataLayer.push('App_Pageview', vpv);
						if (!mobile && item.openTab) {
							globalDispatchContext.openAccordionItem(item.anchor);
						}
					},
				});
			}
			if (mobile) close(false);
		};

		const onClickLogo = () => {
			scrollTo(0, 0.5);
		};

		const renderNavButton = (item, index) => {
			//if(currentIndex == index) console.log('navButton(%o):', item, currentIndex, index);
			return (
				<DataLayer
					key={index}
					onEvent={item.dataLayerId || 'Menu_ClicouItem'}
					args={[item.label]}
				>
				 <button
					className={cls(classes.asideAnchor, {
						[classes.isActive]: currentIndex === index ||

						 (
							// Se o índice for maior que o índice de "tudo da strada" && é o "tudo da strada", seleciona
						 	currentIndex >= MENU_LAST_INDEX && index === MENU_LAST_INDEX
						 ) ||

						(
							// Se eu cliquei no menu "tudo da strada", então marco também o índice do submenu
							currentIndex === MENU_LAST_INDEX && (index === currentTabIndex)
						) || (
							// Se ainda não mudei a aba no DesktopTabs e cliquei em "tudo da strada", seleciona o primeiro subitem
							currentIndex === MENU_LAST_INDEX && currentTabIndex === -1 && index === MENU_LAST_INDEX
						),
					}, item.before)}
					onClick={(evt) => onClickItem(index)}>
					<div className={cls(classes.asideAnchorText, item.submenu)}>
						<div className={cls(classes.asideAnchorMask)} aria-hidden={true}>
							<span dangerouslySetInnerHTML={{ __html: item.label }}/>
						</div>
						<span dangerouslySetInnerHTML={{ __html: item.label }} />
					</div>
				</button>
				</DataLayer>
			);
		};

		const renderNavAnchor = (item, index) => {
			return (
				<DataLayer
					key={index}
					onEvent={item.dataLayerId || 'Menu_ClicouItem'}
					args={[item.label]}
				>
					<a
						className={cls(classes.asideAnchorLink)}
						href={item.href}
						target={item.target}
					>
						{item.icon ? (
							<span
								className={cls(classes.asideAnchorLinkText)}>
								<i className={cls('fa', 'fa-whatsapp')}></i>
								<span dangerouslySetInnerHTML={{ __html: item.label }} />
							</span>
						) : (
							<span
								className={cls(classes.asideAnchorLinkText)}
								dangerouslySetInnerHTML={{ __html: item.label }}
							/>
						)}
					</a>
				</DataLayer>
			);
		};

		const renderNavLink = (item, index) => {
			return (
				<DataLayer
					key={index}
					onEvent={item.dataLayerId || 'Menu_ClicouItem'}
					args={[item.label]}
				>
					<a
						className={cls(classes.asideAnchorLink)}
						href={item.href}
						target={item.target}
					>
						{item.icon ? (
							<span
								className={cls(classes.asideAnchorLinkText)}>
								<span dangerouslySetInnerHTML={{ __html: item.label }} />
							</span>
						) : (
							<span
								className={cls(classes.asideAnchorLinkText)}
								dangerouslySetInnerHTML={{ __html: item.label }}
							/>
						)}
					</a>
				</DataLayer>
			);
		};

		const renderMenuItems = (renderType = '*') => {
			return data.list.map((item, index) => {
				if (item.hideOnMobile && mobile) return null;
				const renderLinks = renderType === '*' || renderType === 'link';
				const renderAnchors = renderType === '*' || renderType === 'link';
				const renderButtons = renderType === '*' || renderType === 'button';
				if (item.router) return renderLinks && renderNavLink(item, index);
				else if (item.href) return renderAnchors && renderNavAnchor(item, index);
				return renderButtons && renderNavButton(item, index);
			});
		};

		const renderResponsiveNavBar = (isOpen) => {
			return (
				<div className={cls(classes.navBar)} ref={navBarRef}>
					{/* HAMBURGER ICON ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/}
					<ToggleButton
						className={cls(classes.navBarLabel)}
						onClick={toggle}
						active={isOpen}
					/>

					{/* FIAT LOGO ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/}
					<DataLayer onEvent={'Menu_ClicouLogoCar'} args={[]}>
						<button className={cls(classes.navBarLogo)} onClick={onClickLogo}>
							<CarBrand className={cls(classes.navBarLogoIcon)} />
						</button>
					</DataLayer>

					{/* PIN ICON ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/}
					{data.pin.visible && (
						<DataLayer onEvent={'Menu_ClicouPin'} args={[]}>
							<a
								className={cls(classes.navBarPin)}
								href={data.pin.href}
								target={data.pin.target}
							>
								<PinIcon className={cls(classes.navBarPinIcon)} />
							</a>
						</DataLayer>
					)}
				</div>
			);
		};

		const renderResponsiveAside = (isOpen) => {
			return (
				<div className={cls(classes.aside)}>
					<div className={cls(classes.asideList)}>
						<div className={cls(classes.asideListAnchors)}>
							{renderMenuItems('button')}
						</div>
						<div className={cls(classes.asideListLinks)}>
							{renderMenuItems('link')}
						</div>
					</div>
				</div>
			);
		};

		const renderDesktopHeader = (isOpen) => {
			return (
				<div className={cls(classes.brand)}>
					<DataLayer onEvent={'Menu_ClicouLogoCar'} args={[]}>
						<CarBrand className={cls(classes.brandCar)} onClick={onClickLogo} />
					</DataLayer>
				</div>
			);
		};

		const renderMobile = (isOpen) => {
			return (
				<React.Fragment>
					<div className={cls(classes.foreground)} onClick={close} />
					{renderResponsiveNavBar(isOpen)}
					{renderResponsiveAside(isOpen)}
				</React.Fragment>
			);
		};

		const renderDesktop = (isOpen) => {
			return (
				<React.Fragment>
					{renderDesktopHeader(isOpen)}
					{renderResponsiveNavBar(isOpen)}
					{renderResponsiveAside(isOpen)}
					{!mobile && (
						<ToggleButton
							className={cls(classes.asideClose)}
							onClick={toggle}
							active={!isOpen}
							slippery={true}
						/>
					)}
				</React.Fragment>
			);
		};

		React.useEffect(() => {
			const count = { progress: 0, total: 1 };
			TweenMax.to(count, 0.75, {
				progress: count.total,
				ease: Expo.easeOut,
				onUpdate: () => {
					const progress = isOpen
						? count.progress
						: count.total - count.progress;
					globalDispatchContext.toggleMenuUpdate(progress);
				},
				onComplete: () => {
					globalDispatchContext.toggleMenuState(isOpen);
				},
			});
		}, [isOpen]);

		React.useEffect(() => {
			document.body.classList[isOpen ? 'add' : 'remove'](classes.globalOpen);
			document.body.classList[isShrink ? 'add' : 'remove'](
				classes.globalShrink
			);
		}, [isOpen, isShrink]);

		React.useEffect(() => {
			window.addEventListener('scroll', onScroll);
			return () => window.removeEventListener('scroll', onScroll);
		}, [mobile]);

		React.useEffect(() => {
			const index = data.list.findIndex(
				(item) => item.anchor === globalStateContext.menuSelectedAnchor
			);
			if (index > -1) {
				setCurrentTabIndex(index);
				onClickItem(index);
			}
		}, [globalStateContext.menuSelectedAnchor]);

		React.useImperativeHandle(ref, () => ({
			root: rootRef.current,
		}));

		return (
			<div
				ref={rootRef}
				className={cls(classes.root, {
					[classes.isOnTop]: mobile && isOnTop,
					[classes.isInvisible]: mobile && !isOpen && !isCloseToTop && isInvisible,
					[classes.isShrink]: mobile && isShrink,
					[classes.isOpen]: isOpen,
				})}
			>
				{mobile ? renderMobile(isOpen) : renderDesktop(isOpen)}
			</div>
		);
	}
);

Menu.displayName = 'Menu';
Menu.propTypes = propTypes;
Menu.defaultProps = defaultProps;
export default React.memo(Menu);
